'use client';
import { isFirefoxDefaultSearch } from '#pplx/frontend/apps/ask/browser/firefoxUtils.js';
import { isMaybeIncognitoComet } from '#pplx/frontend/apps/ask/comet-shared/cometUtils.js';
import { useWebHardLoginGateVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useWebHardLoginGateVariation.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useIsUserLoggedIn, useIsUserLoggedInLoading } from '#pplx/frontend/auth/userHooks.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useMemo } from 'react';
/**
 * Shared hook that wraps useWebHardLoginGateVariation with local eligibility checks.
 *
 * This minimizes Eppo exposure calls for cleaner experiment data by:
 * 1. Waiting for auth status to resolve (prevents premature Eppo calls)
 * 2. Excluding ineligible users (logged in, mobile, partner search, Samsung browser, special capabilities)
 * 3. Waiting until the point when a treatment condition may be applied (3rd visitor daily query count)
 */
export const useHardLoginGateVariation = ({ visitorDailyQueryCount, }) => {
    const isUserLoggedIn = useIsUserLoggedIn();
    const isUserLoggedInLoading = useIsUserLoggedInLoading();
    const { isMobileUserAgent } = useIsMobile();
    const { device: { isSamsungBrowser }, } = useWebPlatform();
    const { specialCapabilities } = useUserCapabilities();
    const isComet = isMaybeIncognitoComet();
    const isFirefoxPartnerSearch = isFirefoxDefaultSearch();
    const skipAssignment = isUserLoggedInLoading ||
        isUserLoggedIn ||
        isMobileUserAgent ||
        isSamsungBrowser ||
        isComet ||
        isFirefoxPartnerSearch ||
        specialCapabilities.unlimitedProSearch ||
        visitorDailyQueryCount === undefined ||
        visitorDailyQueryCount <= 2;
    // The hard-login gate only has product impact at the eligible visitor query
    // count; all earlier or ineligible states stay control without exposure.
    const { variation, loading } = useWebHardLoginGateVariation({
        defaultValue: 'control',
        skip: skipAssignment,
    });
    return useMemo(() => ({ variation, loading }), [variation, loading]);
};
//# sourceMappingURL=useHardLoginGateVariation.js.map